import * as pdfjsLib from 'pdfjs-dist';
import { AttachmentType } from '../config';
import {
    EXTRACT_TEXT_HEIGHT_PERCENTAGE,
    EXTRACT_TEXT_WIDTH_PERCENTAGE,
    SCALEFACTOR_FOR_CONVERSION,
} from '../constant';

export class Utils {
    static async readPdf(input) {
        let pdfBuffer;
        if (input instanceof Blob) {
            pdfBuffer = await input.arrayBuffer();
        } else {
            pdfBuffer = input;
        }

        try {
            const typedArray = new Uint8Array(pdfBuffer); // No need to use FileReader
            const pdfDoc = await pdfjsLib.getDocument(typedArray).promise;
            return pdfDoc;
        } catch (error) {
            throw new Error('Error loading PDF:', error);
        }
    }

    static generateUuid() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }
    static isPointOnLine(point, lineStart, lineEnd, tol = 0.001) {
        if (
            !point ||
            !point.x ||
            !point.y ||
            !lineStart ||
            !lineEnd ||
            !lineStart.x ||
            !lineStart.y ||
            !lineEnd.x ||
            !lineEnd.y
        ) {
            return false;
        }
        if (
            Math.abs(point.x - lineStart.x) < tol &&
            Math.abs(point.y - lineStart.y) < tol
        )
            return true;
        if (
            Math.abs(point.x - lineEnd.x) < tol &&
            Math.abs(point.y - lineEnd.y) < tol
        )
            return true;
        const dist1 = Math.sqrt(
            (point.x - lineStart.x) ** 2 + (point.y - lineStart.y) ** 2,
        );
        const dist2 = Math.sqrt(
            (point.x - lineEnd.x) ** 2 + (point.y - lineEnd.y) ** 2,
        );
        const dist3 = Math.sqrt(
            (lineStart.x - lineEnd.x) ** 2 + (lineStart.y - lineEnd.y) ** 2,
        );
        return dist1 + dist2 - dist3 < tol;
    }
    static isPointOnLineSegment(point, start, end, tolerance = 1e-6) {
        const dx = end.x - start.x;
        const dy = end.y - start.y;
        const lenSq = dx * dx + dy * dy;
        if (lenSq < tolerance * tolerance) {
            const distSq = (point.x - start.x) ** 2 + (point.y - start.y) ** 2;
            return distSq <= tolerance * tolerance;
        }
        const cross = (point.y - start.y) * dx - (point.x - start.x) * dy;
        if (Math.abs(cross) > tolerance * Math.sqrt(lenSq)) {
            return false;
        }
        const dot = (point.x - start.x) * dx + (point.y - start.y) * dy;
        return (
            dot >= -tolerance * Math.sqrt(lenSq) &&
            dot <= lenSq + tolerance * Math.sqrt(lenSq)
        );
    }
    static isPointCollinear(point, start, end, tolerance = 1e-6) {
        const dx = end.x - start.x;
        const dy = end.y - start.y;
        const lenSq = dx * dx + dy * dy;
        if (lenSq < tolerance * tolerance) {
            const distSq = (point.x - start.x) ** 2 + (point.y - start.y) ** 2;
            return distSq <= tolerance * tolerance;
        }
        const cross = (point.y - start.y) * dx - (point.x - start.x) * dy;
        const len = Math.sqrt(lenSq);
        return Math.abs(cross) <= tolerance * len;
    }

    static doLineSegmentsOverlap(p1, p2, q1, q2) {
        if (Utils.doSegmentsIntersect(p1, p2, q1, q2)) return true;

        return (
            Utils.isPointOnLineSegment(p1, q1, q2) ||
            Utils.isPointOnLineSegment(p2, q1, q2) ||
            Utils.isPointOnLineSegment(q1, p1, p2) ||
            Utils.isPointOnLineSegment(q2, p1, p2)
        );
    }

    static doSegmentsIntersect(p1, p2, q1, q2) {
        const orientation = (a, b, c) => {
            const val = (b.y - a.y) * (c.x - b.x) - (b.x - a.x) * (c.y - b.y);
            if (Math.abs(val) < 1e-6) return 0;
            return val > 0 ? 1 : 2;
        };

        const o1 = orientation(p1, p2, q1);
        const o2 = orientation(p1, p2, q2);
        const o3 = orientation(q1, q2, p1);
        const o4 = orientation(q1, q2, p2);

        if (o1 !== o2 && o3 !== o4) return true;

        return (
            Utils.isPointOnLineSegment(q1, p1, p2) ||
            Utils.isPointOnLineSegment(q2, p1, p2) ||
            Utils.isPointOnLineSegment(p1, q1, q2) ||
            Utils.isPointOnLineSegment(p2, q1, q2)
        );
    }

    static pointInPolygon(point, polygon) {
        const { x, y } = point;
        let isInside = false;

        for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
            const { x: xi, y: yi } = polygon[i];
            const { x: xj, y: yj } = polygon[j];

            const intersect =
                yi > y !== yj > y &&
                x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
            if (intersect) isInside = !isInside;
        }

        return isInside;
    }
    static isClockwise = (points) => {
        let sum = 0;
        for (let i = 0; i < points.length; i++) {
            const current = points[i];
            const next = points[(i + 1) % points.length];
            sum += (next.x - current.x) * (next.y + current.y);
        }
        return sum < 0; // Positive sum means clockwise, negative means counterclockwise
    };

    static translateClosetToCenter = (coordinates) => {
        // Assuming coordinates is an array of {x, y} objects
        if (!coordinates || coordinates.length === 0) return coordinates;

        // Calculate centroid (average position)
        let minX = Infinity,
            maxX = -Infinity;
        let minY = Infinity,
            maxY = -Infinity;

        coordinates.forEach((point) => {
            minX = Math.min(minX, point.x);
            maxX = Math.max(maxX, point.x);
            minY = Math.min(minY, point.y);
            maxY = Math.max(maxY, point.y);
        });

        const centerX = (minX + maxX) / 2;
        const centerY = (minY + maxY) / 2;

        let transformedCoords = coordinates.map((point) => ({
            x: (point.x - centerX) / SCALEFACTOR_FOR_CONVERSION,
            y: -(point.y - centerY) / SCALEFACTOR_FOR_CONVERSION,
        }));

        // Check winding order and reverse if counterclockwise
        if (this.isClockwise(transformedCoords)) {
            transformedCoords = transformedCoords.reverse();
        }

        // transformedCoords = this.arrangePoints(transformedCoords);

        return {
            points: transformedCoords,
            transX: centerX,
            transY: centerY,
        };
    };

    static isSelfIntersect = (points, index = 0) => {
        if (points.length < 4) return false;

        for (let i = 0; i < points.length - index; i++) {
            const p1 = points[i];
            const p2 = points[(i + 1) % points.length];
            for (let j = i + 2; j < points.length - index; j++) {
                if (j === i + points.length - 1 && i === 0) continue;
                if (i === points.length - 1 && j === points.length - 1)
                    continue;
                const p3 = points[j];
                const p4 = points[(j + 1) % points.length];
                if (Utils.isLineIntersect(p1, p2, p3, p4)) {
                    return true;
                }
            }
        }
        return false;
    };

    static arrangePoints(inPoints, startIndex) {
        if (startIndex >= 0) {
            return [
                ...inPoints.slice(startIndex),
                ...inPoints.slice(0, startIndex),
            ];
        }
        let minIndex = 0;
        let minY = inPoints[0].y;

        for (let i = 1; i < inPoints.length; i++) {
            if (
                inPoints[i].y < minY ||
                (inPoints[i].y === minY && inPoints[i].x < inPoints[minIndex].x)
            ) {
                minY = inPoints[i].y;
                minIndex = i;
            }
        }

        return [...inPoints.slice(minIndex), ...inPoints.slice(0, minIndex)];
    }

    static isLineIntersect = (p1, p2, p3, p4) => {
        const denominator =
            (p4.y - p3.y) * (p2.x - p1.x) - (p4.x - p3.x) * (p2.y - p1.y);
        const numerator1 =
            (p4.x - p3.x) * (p1.y - p3.y) - (p4.y - p3.y) * (p1.x - p3.x);
        const numerator2 =
            (p2.x - p1.x) * (p1.y - p3.y) - (p2.y - p1.y) * (p1.x - p3.x);

        if (denominator === 0) {
            if (numerator1 !== 0 || numerator2 !== 0) return false;
            if (p1.x === p2.x) {
                const t0 = (p3.y - p1.y) / (p2.y - p1.y);
                const t1 = (p4.y - p1.y) / (p2.y - p1.y);
                const s0 = (p1.y - p3.y) / (p4.y - p3.y);
                const s1 = (p2.y - p3.y) / (p4.y - p3.y);
                return (
                    (t0 >= 0 && t0 <= 1 && s0 >= 0 && s0 <= 1) ||
                    (t1 >= 0 && t1 <= 1 && s1 >= 0 && s1 <= 1) ||
                    (s0 >= 0 && s0 <= 1 && t0 >= 0 && t0 <= 1) ||
                    (s1 >= 0 && s1 <= 1 && t1 >= 0 && t1 <= 1)
                );
            } else {
                const t0 = (p3.x - p1.x) / (p2.x - p1.x);
                const t1 = (p4.x - p1.x) / (p2.x - p1.x);
                const s0 = (p1.x - p3.x) / (p4.x - p3.x);
                const s1 = (p2.x - p3.x) / (p4.x - p3.x);
                return (
                    (t0 >= 0 && t0 <= 1 && s0 >= 0 && s0 <= 1) ||
                    (t1 >= 0 && t1 <= 1 && s1 >= 0 && s1 <= 1) ||
                    (s0 >= 0 && s0 <= 1 && t0 >= 0 && t0 <= 1) ||
                    (s1 >= 0 && s1 <= 1 && t1 >= 0 && t1 <= 1)
                );
            }
        }

        const r = numerator1 / denominator;
        const s = numerator2 / denominator;
        return r >= 0 && r <= 1 && s >= 0 && s <= 1;
    };

    static parseInches(value) {
        if (typeof value !== 'string' || !value.trim()) {
            console.warn(`Invalid value for parseInches: ${value}`);
            return 0;
        }
        const match = value.match(/(\d+)' ([\d.]+|"?\d+"?)/);
        if (!match) {
            console.warn(`parseInches failed for value: ${value}`);
            return 0;
        }
        const feet = parseInt(match[1], 10) || 0;
        const inches = parseFloat(match[2].replace('"', '')) || 0;
        return parseFloat((feet * 12 + inches).toFixed(2));
    }

    static translateAttachments = (coordinates, transX, transY) => {
        let transformedCoords = coordinates.map((point) => ({
            x: (point.x - transX) / SCALEFACTOR_FOR_CONVERSION,
            y: -(point.y - transY) / SCALEFACTOR_FOR_CONVERSION,
        }));
        return transformedCoords;
    };

    static calcDistance = (p1, p2) => {
        return (
            Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2) /
            SCALEFACTOR_FOR_CONVERSION
        );
    };
    static translateObstacles = (coordinates, transX, transY) => {
        let transformedCoords = {
            x: (coordinates.x - transX) / SCALEFACTOR_FOR_CONVERSION,
            y: -(coordinates.y - transY) / SCALEFACTOR_FOR_CONVERSION,
        };
        return transformedCoords;
    };

    static isPointInTol = (p1, p2, tol) => {
        return this.calcDistance(p1, p2) <= tol;
    };
    static getDoorWall(translatedAttachments, points) {
        const wallIndex = [];
        for (let i = 0; i < points.length; i++) {
            translatedAttachments.forEach((attachment) => {
                if (
                    attachment &&
                    attachment._coordinates &&
                    Array.isArray(attachment._coordinates) &&
                    attachment._coordinates[0]
                ) {
                    if (
                        Utils.isPointOnLine(
                            attachment._coordinates[0],
                            { x: points[i].x, y: points[i].y },
                            {
                                x: points[(i + 1) % points.length].x,
                                y: points[(i + 1) % points.length].y,
                            },
                        ) &&
                        attachment._attachmentType === AttachmentType.DOOR
                    ) {
                        wallIndex.push({
                            p1: i,
                            p2: (i + 1) % points.length,
                            length: Utils.calcDistance(
                                points[i],
                                points[(i + 1) % points.length],
                            ),
                        });
                    }
                }
            });
        }
        const longestWall = wallIndex.reduce((prev, current) =>
            prev.length > current.length ? prev : current,
        );

        return longestWall;
    }

    static getAngleFromP1ToP2(p1, p2) {
        const dx = p2.x - p1.x;
        const dy = p2.y - p1.y;
        if (dx === 0 && dy === 0) throw new Error('Wall length must be > 0');
        return Math.atan2(dy, dx);
    }

    static rotatePoint({ x, y }, angle) {
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        return {
            x: x * cos - y * sin,
            y: x * sin + y * cos,
        };
    }

    static rotatePoints(points, angle) {
        return points.map((p) => this.rotatePoint(p, angle));
    }

    static getMinY(points) {
        return Math.min(...points.map((p) => p.y));
    }

    static translatePointsY(points, deltaY) {
        return points.map((p) => ({
            x: p.x,
            y: p.y - deltaY,
        }));
    }

    static rotateAndPlaceWallBelow(
        p1,
        p2,
        points,
        attachments,
        obstacles,
        internalWalls,
    ) {
        const wallAngle = this.getAngleFromP1ToP2(p1, p2);
        const rotateAngle = Math.PI - wallAngle;

        const rotatedPoints = this.rotatePoints(points, rotateAngle);
        const rotatedP1 = this.rotatePoint(p1, rotateAngle);
        const rotatedP2 = this.rotatePoint(p2, rotateAngle);

        const wallY = Math.min(rotatedP1.y, rotatedP2.y);
        const sceneMinY = this.getMinY(rotatedPoints);
        const offsetY = wallY - sceneMinY;

        attachments?.forEach((attachment) => {
            const updatedPoints = this.rotatePoints(
                attachment._coordinates,
                rotateAngle,
            );
            const updatedAttachment = this.translatePointsY(
                updatedPoints,
                offsetY,
            );
            attachment._coordinates = updatedAttachment;
        });
        obstacles?.forEach((obstacle) => {
            const rotated = this.rotatePoints(
                [obstacle._data.coordinate],
                rotateAngle,
            );

            obstacle._data.coordinate = this.translatePointsY(
                rotated,
                offsetY,
            )[0];
        });
        internalWalls?.forEach((wall) => {
            const rotated = this.rotatePoints([wall._coordinate], rotateAngle);
            wall._coordinate = this.translatePointsY(rotated, offsetY)[0];
        });
        const updatedPoints = this.translatePointsY(rotatedPoints, offsetY);
        return {
            updatedPoints,
            updatedAttachments: attachments,
            updatedObstacles: obstacles,
            rotateAngle,
            updatedInternalWalls: internalWalls,
        };
    }

    static async getArrayFromBinFile(binFileUrl, name) {
        const binFile = await fetch(binFileUrl);
        const arrayBuffer = await binFile.arrayBuffer();
        const floatArray = new Float32Array(arrayBuffer);
        const regularArray = Array.from(floatArray);
        return { [name]: regularArray };
    }
    static async extractTextFromCustomBox(
        rightPercentage = EXTRACT_TEXT_WIDTH_PERCENTAGE,
        bottomPercentage = EXTRACT_TEXT_HEIGHT_PERCENTAGE,
        page,
    ) {
        const textContent = await page.getTextContent();
        // PDF viewport to determine dimensions
        const viewport = page.getViewport({
            scale: 1,
        });
        const pageWidth = viewport.width;
        const pageHeight = viewport.height;
        // Calculate boundary coordinates
        const minY = 0;
        const maxY = pageHeight * bottomPercentage;
        const minX = pageWidth * (1 - rightPercentage);
        const maxX = pageWidth;
        const lineMap = {};
        const linePositions = {};
        for (const item of textContent.items) {
            const x = item.transform[4];
            const y = Math.round(item.transform[5]);

            if (!lineMap[y]) {
                lineMap[y] = [];
                linePositions[y] = { minX: x, maxX: x };
            }

            lineMap[y].push(item.str);

            // Track X bounds for the line
            linePositions[y].minX = Math.min(linePositions[y].minX, x);
            linePositions[y].maxX = Math.max(linePositions[y].maxX, x);
        }

        // Sort by Y descending (top to bottom)
        const sortedLines = Object.entries(lineMap).sort((a, b) => b[0] - a[0]);

        const textBlocks = sortedLines.map(([y, lineItems]) => {
            return {
                y: parseInt(y),
                text: lineItems.join(' '),
                minX: linePositions[y].minX,
                maxX: linePositions[y].maxX,
                height: maxY - minY,
            };
        });

        const scalePatterns = [
            // 1/4" = 1'-0"
            /(\d+)\s*\/\s*(\d+)\s*["”]?\s*=\s*(\d+)'\s*-?\s*(\d+)["”]?/,
            // 1/4" = 1'
            /(\d+)\s*\/\s*(\d+)\s*["”]?\s*=\s*(\d+)'/,
            // 1/4 IN = 1 FT
            /(\d+)\s*\/\s*(\d+)\s*(?:in|IN)\s*=\s*(\d+)\s*(?:ft|FT)/,
            // Pattern for scales like: 1/4 IN = 1 FT or 1/8 IN = 1 FT
            /(\d+)\s*\/\s*(\d+)\s*(?:in|IN)\s*=\s*(\d+)\s*(?:ft|FT)/,
            // 1/4" = 1FT
            /(\d+)\s*\/\s*(\d+)\s*["”]?\s*=\s*(\d+)\s*(?:ft|FT)/,
            // 1:50 (metric-style)
            /\b(1)\s*:\s*(\d{1,3})\b(?![:\d])/g,
        ];

        const normalizeText = (text) => {
            return text
                .replace(/\s+/g, '')
                .replace(/[""]/g, '"')
                .replace(/['']/g, "'")
                .replace(/[‐–—−]/g, '-')
                .toUpperCase();
        };

        for (const block of textBlocks) {
            const { text, minX: blockMinX, maxX: blockMaxX, y } = block;

            const isInBottomRight =
                y <= maxY && // Within bottom 20% (vertical bound)
                blockMinX >= minX && // Text starts in right 30% (horizontal bound)
                blockMaxX <= maxX; // Text ends within page width

            const hasOverlapWithBottomRight =
                y <= maxY && // Within bottom 20%
                blockMaxX >= minX && // Text extends into right 30%
                blockMinX <= maxX; // Text starts before page end

            if (!isInBottomRight && !hasOverlapWithBottomRight) continue;

            const normalizedBlock = normalizeText(text);
            for (const pattern of scalePatterns) {
                const match =
                    text.match(pattern) || normalizedBlock.match(pattern);
                if (match) {
                    const numerator = parseInt(match[1], 10);
                    const denominator = parseInt(match[2], 10);
                    if (!numerator || !denominator) continue;

                    const decimalValue = numerator / denominator;
                    return decimalValue;
                }
            }
        }
    }

    static getPolygonCenter(points) {
        const sum = points.reduce(
            (acc, point) => ({
                x: acc.x + point.x,
                y: acc.y + point.y,
            }),
            { x: 0, y: 0 },
        );

        return {
            x: sum.x / points.length,
            y: sum.y / points.length,
        };
    }
}
